<script setup lang="ts">
import { useIsDesktop } from '@apexx/common-composables';
import { AnimatePresence, motion } from 'motion-v';
import { computed, useSlots } from 'vue';

import Icon from '../icon/Icon.vue';

import { useClose } from '../../composables/useClose';

type Placement = 'left' | 'right' | 'bottom' | 'center';

export type DrawerDesktopMode = 'aside' | 'drawer' | 'dialog' | 'bottom';

export interface drawerProps {
	open: boolean;
	title?: string;
	subTitle?: string;
	placement?: Placement;
	width?: string;
	height?: string;
	persistent?: boolean;
	showClose?: boolean;
	closeOnOverlay?: boolean;
	/** When placement is bottom, tapping the top handle dismisses the drawer. */
	dismissOnHandle?: boolean;
	/**
	 * Desktop presentation when `placement` is `bottom` (≥ `lg`).
	 * - `dialog` (default): centered modal
	 * - `aside`: right side panel
	 * - `drawer`: bottom sheet with desktop classes
	 * - `bottom`: opt out — keep bottom sheet, no desktop classes
	 */
	desktopMode?: DrawerDesktopMode;
	/** Classes applied to the panel only in desktop adaptive mode (`desktopMode !== 'bottom'`). */
	desktopClasses?: string;
}

const props = withDefaults(defineProps<drawerProps>(), {
	placement: 'right',
	width: '420px',
	height: '40vh',
	persistent: false,
	showClose: true,
	closeOnOverlay: true,
	dismissOnHandle: true,
	desktopMode: 'dialog',
	desktopClasses: '',
});

const emit = defineEmits<{
	'update:open': [value: boolean];
	close: [];
}>();

const isDesktop = useIsDesktop();

const isDesktopModeActive = computed(
	() =>
		props.placement === 'bottom'
		&& isDesktop.value
		&& props.desktopMode !== 'bottom',
);

const effectivePlacement = computed<Placement>(() => {
	if (props.placement !== 'bottom' || !isDesktop.value) {
		return props.placement;
	}

	switch (props.desktopMode) {
		case 'aside':
			return 'right';
		case 'dialog':
			return 'center';
		case 'drawer':
		case 'bottom':
			return 'bottom';
	}
});

function closeDrawer() {
	if (props.persistent) {
		return;
	}

	emit('update:open', false);
	emit('close');
}

function handleBackdrop() {
	if (!props.closeOnOverlay) {
		return;
	}

	closeDrawer();
}

function handleGrabberClick() {
	if (!props.dismissOnHandle || effectivePlacement.value !== 'bottom') {
		return;
	}

	closeDrawer();
}

useClose(
	() => {
		if (props.persistent) {
			return true;
		}

		closeDrawer();
		return true;
	},
	computed(() => props.open),
);

const isBottom = computed(() => effectivePlacement.value === 'bottom');
const isDocked = computed(() => effectivePlacement.value !== 'center');
const showHeaderClose = computed(
	() => props.showClose && !isBottom.value,
);

const overlayClasses = computed(() =>
	effectivePlacement.value === 'center'
		? 'flex items-center justify-center p-5'
		: '',
);

const panelPositionClasses = computed(() => {
	switch (effectivePlacement.value) {
		case 'left':
			return 'left-0 top-0 h-screen rounded-r-lg';
		case 'right':
			return 'right-0 top-0 h-screen rounded-l-lg';
		case 'bottom':
			return 'inset-x-0 bottom-0 mx-auto w-full max-w-2xl rounded-t-2xl';
		case 'center':
			return 'max-h-[90vh] w-full rounded-lg';
	}
});

const panelMotion = computed(() => {
	switch (effectivePlacement.value) {
		case 'left':
			return {
				initial: { x: '-100%' },
				animate: { x: 0 },
				exit: { x: '-100%' },
			};
		case 'right':
			return {
				initial: { x: '100%' },
				animate: { x: 0 },
				exit: { x: '100%' },
			};
		case 'bottom':
			return {
				initial: { y: '100%' },
				animate: { y: 0 },
				exit: { y: '100%' },
			};
		case 'center':
			return {
				initial: { opacity: 0, scale: 0.95 },
				animate: { opacity: 1, scale: 1 },
				exit: { opacity: 0, scale: 0.95 },
			};
	}
});

const panelTransition = computed(() =>
	effectivePlacement.value === 'bottom'
		? { type: 'spring' as const, damping: 32, stiffness: 380 }
		: { type: 'spring' as const, damping: 30, stiffness: 400 },
);

const overlayTransition = { duration: 0.2 };

const panelStyle = computed(() => {
	const safeAreaBottom = { paddingBottom: 'var(--safe-area-bottom)' };

	switch (effectivePlacement.value) {
		case 'left':
		case 'right':
			return { width: props.width, ...safeAreaBottom };
		case 'bottom':
			if (props.height === 'full') {
				return { maxHeight: '92dvh', ...safeAreaBottom };
			}
			return { height: props.height, ...safeAreaBottom };
		case 'center':
			return { width: props.width, ...safeAreaBottom };
	}
});

const panelClassList = computed(() => [
	'flex flex-col overflow-hidden bg-surface shadow-md',
	isDocked.value ? 'fixed z-10' : '',
	panelPositionClasses.value,
	effectivePlacement.value === 'bottom' ? 'max-w-full' : 'max-lg:max-w-sm',
	isDesktopModeActive.value ? props.desktopClasses : '',
]);

const slots = useSlots();

const showHeader = computed(() =>
	Boolean(
		props.title
			|| props.subTitle
			|| showHeaderClose.value
			|| slots.header,
	),
);

const hasFooter = computed(() => Boolean(slots.footer));
</script>

<template>
	<Teleport to="body">
		<AnimatePresence>
			<motion.div
				v-if="open"
				key="drawer-overlay"
				class="fixed inset-0 z-50 bg-surface-overlay"
				:class="overlayClasses"
				:initial="{ opacity: 0 }"
				:animate="{ opacity: 1 }"
				:exit="{ opacity: 0 }"
				:transition="overlayTransition"
				@click.self="handleBackdrop"
			>
				<motion.div
					key="drawer-panel"
					:class="panelClassList"
					:style="panelStyle"
					:initial="panelMotion.initial"
					:animate="panelMotion.animate"
					:exit="panelMotion.exit"
					:transition="panelTransition"
					role="dialog"
					aria-modal="true"
					:aria-label="title"
				>
					<button
						v-if="isBottom"
						type="button"
						class="flex w-full shrink-0 cursor-pointer items-center justify-center py-3 touch-manipulation"
						:aria-label="dismissOnHandle ? 'Close drawer' : 'Drawer handle'"
						:disabled="!dismissOnHandle"
						@click="handleGrabberClick"
					>
						<span class="h-1 w-12 rounded-full bg-border" />
					</button>

					<div
						v-if="showHeader"
						class="flex shrink-0 items-start justify-between gap-3 px-5 pb-4"
						:class="isBottom ? 'pt-0' : 'pt-4'"
					>
						<div class="min-w-0 flex-1">
							<slot name="header">
								<h2
									v-if="title"
									class="text-title font-semibold text-text"
								>
									{{ title }}
								</h2>
								<p
									v-if="subTitle"
									class="mt-1 text-body text-on-surface-muted"
								>
									{{ subTitle }}
								</p>
							</slot>
						</div>

						<button
							v-if="showHeaderClose"
							type="button"
							class="shrink-0 rounded-md p-xs text-on-surface-muted transition-colors hover:bg-primary-light hover:text-primary"
							aria-label="Close drawer"
							@click="closeDrawer"
						>
							<Icon name="cancel" size="md" />
						</button>
					</div>

					<div
						class="flex-1 overflow-y-auto px-5"
						:class="hasFooter || isBottom ? 'pb-4 pt-1' : 'py-5'"
					>
						<slot />
					</div>

					<div
						v-if="hasFooter"
						class="shrink-0 bg-surface px-5 pb-6 pt-4"
					>
						<slot name="footer" />
					</div>
				</motion.div>
			</motion.div>
		</AnimatePresence>
	</Teleport>
</template>
